/**
 * Thin wrapper around fetch for the /api/v1/meetings endpoints.
 * Reads the auth token from localStorage via a lazy getter so we don't
 * need to wire up the full store here.
 */
import { config } from '@/config/env';
import type { RoomCredentials } from '@/features/meeting/store/meeting-session.store'
import { retryWhileSignalingBusy } from './signaling-busy-retry';

const API = config.apiUrl;

function getToken(): string {
  try {
    const raw = localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session');
    if (raw) {
      const parsed = JSON.parse(raw) as any;
      const session = parsed?.session;
      if (session?.expiresAt && session.expiresAt <= Date.now()) return '';
      return session?.accessToken || '';
    }
  } catch {}
  return '';
}

export class ApiError extends Error {
  code: string;
  status: number;
  /** The rest of the server's `error` object, e.g. `nextStartTimeMs` on a series refusal. */
  details: Record<string, unknown>;
  constructor(message: string, code: string, status: number, details: Record<string, unknown> = {}) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      body?.error?.message || `API error ${res.status}`,
      body?.error?.code || 'API_ERROR',
      res.status,
      body?.error && typeof body.error === 'object' ? body.error : {},
    );
  }
  return res.json() as Promise<T>;
}

/** One archived group message, as the meeting-chat tables store it. */
export interface MeetingChatHistoryMessage {
  id: string
  /** Sender's peerId (= the signed-in user's id for a signed-in participant). */
  from: string
  fromName: string | null
  text: string
  attachment: { kind: string; url: string; name: string; size: number; mimeType?: string } | null
  /** Epoch ms. */
  ts: number
}

/** One finalized speech-to-text segment, as the transcription AI server pushed it. */
export interface MeetingTranscriptSegment {
  id: string
  speakerId: string
  speakerName: string | null
  text: string
  /** Epoch ms. */
  ts: number
}

export const meetingsApi = {
  list(params?: { date?: string; status?: string; from?: number; to?: number }) {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v != null)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return apiFetch<{ meetings: any[]; total: number }>(`/api/v1/meetings${qs}`);
  },

  getById(id: string) {
    return apiFetch<{ meeting: any }>(`/api/v1/meetings/${id}`);
  },

  /**
   * The meeting's group chat, after it ended.
   *
   * Group only — private threads are not part of a meeting's record. The server
   * resolves the room from the meeting and checks the caller hosted or attended
   * it, so nothing here needs a room id or a room token.
   */
  getChatHistory(id: string, limit = 200) {
    return apiFetch<{ messages: MeetingChatHistoryMessage[] }>(
      `/api/v1/meetings/${encodeURIComponent(id)}/chat?limit=${limit}`,
    );
  },

  /**
   * The meeting's transcript, after it ended.
   *
   * Same authorization as chat history — host or attendee, no room id or room
   * token needed. Empty for any meeting held before this feature shipped, or
   * one nobody spoke in.
   */
  getTranscripts(id: string, limit = 1000) {
    return apiFetch<{ transcripts: MeetingTranscriptSegment[] }>(
      `/api/v1/meetings/${encodeURIComponent(id)}/transcripts?limit=${limit}`,
    );
  },

  /**
   * A link to one of the meeting's scheduled files — for its host, invitees,
   * co-hosts and presenters only (VAPT 2026-10 §6.2). A private file's link is signed and
   * lapses at `expiresAt`; a file stored before that change comes back with its
   * public URL and no expiry. Ask at the moment of use, never at page load.
   */
  getAttachmentUrl(id: string, attachmentId: string) {
    return apiFetch<{ url: string | null; expiresAt: string | null }>(
      `/api/v1/meetings/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}/url`,
    );
  },

  createInstant(body: {
    title: string;
    duration_minutes: number;
    seats: number;
    simulcast_enabled?: boolean;
    /** Saved with the meeting as sent, like a scheduled meeting's. */
    settings?: Record<string, unknown>;
  }) {
    return apiFetch<{ meeting: any; roomCredentials: any }>('/api/v1/meetings/instant', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  schedule(body: Record<string, unknown>) {
    return apiFetch<{ meeting: any }>('/api/v1/meetings', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** `scope: 'all'` on a day of a series edits every day that has not happened yet. */
  update(id: string, body: Record<string, unknown>, opts: { scope?: 'one' | 'all' } = {}) {
    const query = opts.scope === 'all' ? '?scope=all' : '';
    return apiFetch<{ meeting: any }>(`/api/v1/meetings/${id}${query}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  /**
   * A locked room answers `status: 'waiting'` with a request id in place of
   * credentials — signed-in users are gated the same as guests, so callers must
   * handle both arms.
   */
  /**
   * `check: true` runs the join's checks (exists, not ended, password) and
   * stops there: no lobby request, no recorded join, no credentials. Answered
   * with `status: 'ok'`. A server that predates it ignores the flag and joins.
   */
  join(id: string, body: { password?: string; key?: string; check?: boolean }) {
    // Retried while signaling is too busy to mint a room token — see
    // signaling-busy-retry.
    return retryWhileSignalingBusy(() => apiFetch<{
      status?: 'admitted' | 'waiting' | 'ok'
      requestId?: string
      /** Why they wait: `'host'` = the host has not arrived yet, `'admission'` = the host must let them in. */
      waitingFor?: WaitingFor
      /** Where the decision is pushed, when the server can push it. */
      lobby?: LobbyWait
      meeting: any
      roomCredentials?: any
      /**
       * Which audience the join key placed this person in. Absent on a meeting
       * without passive attendees, which is every meeting by default.
       *
       * Reported so the UI can stop offering camera and microphone it knows
       * will be refused — not so the client can decide. The rule itself lives
       * in the room token the server minted alongside this answer.
       */
      mode?: 'active' | 'passive'
    }>(`/api/v1/meetings/${id}/join`, {
      method: 'POST',
      body: JSON.stringify(body),
    }));
  },

  /**
   * Join as a screen only — the dashboard's Screen Share.
   *
   * A mode of its own rather than a flag on `join`: no password, its own
   * identity (`shareId`), a token the server marks share-only, and a meeting
   * summary instead of the full record. `check: true` asks "could I?" the same
   * way `join` does, filing no lobby request. Contract §6 in
   * docs/screen-share-only-join-checkpoint.md.
   *
   * A server that predates share mode answers as an ordinary join, without
   * `mode: 'share'`. Callers must refuse that answer rather than use it.
   */
  joinShare(id: string, body: { displayName?: string; check?: boolean }) {
    return retryWhileSignalingBusy(() => apiFetch<ShareJoinResult>(
      `/api/v1/meetings/${encodeURIComponent(id)}/join`,
      { method: 'POST', body: JSON.stringify({ mode: 'share', ...body }) },
    ));
  },

  /**
   * Close a running meeting off now so it moves to Past.
   *
   * Refused with MEETING_OCCUPIED while anyone is still in the room.
   * `endSession` disconnects them first (the same `meeting:ended` as
   * `endForAll`) and then marks it past, in one request.
   */
  markPast(id: string, opts: { endSession?: boolean } = {}) {
    return apiFetch<{ meeting: any }>(`/api/v1/meetings/${id}/mark-past`, {
      method: 'POST',
      body: JSON.stringify({ endSession: opts.endSession === true }),
    });
  },

  /**
   * Credentials for a breakout room of this meeting.
   *
   * A breakout is a room of its own, so moving into one is the same act as
   * joining a meeting: take its credentials and connect. The client's whole
   * media stack derives from the credentials it holds, so swapping them is the
   * move.
   */
  /**
   * Credentials for the meeting's main room — the way back out of a breakout.
   *
   * Used when this client has no remembered main credentials, which is any
   * client that reloaded while inside a room.
   */
  mainRoomCredentials(meetingId: string, roomToken: string) {
    return apiFetch<{ roomCredentials: RoomCredentials }>(
      `/api/v1/meetings/${meetingId}/main/credentials`,
      { method: 'POST', headers: { Authorization: `Bearer ${roomToken}` } },
    );
  },

  breakoutCredentials(meetingId: string, breakoutId: string, roomToken: string) {
    return apiFetch<{ roomCredentials: RoomCredentials }>(
      `/api/v1/meetings/${meetingId}/breakout/${encodeURIComponent(breakoutId)}/credentials`,
      {
        method: 'POST',
        // The ROOM token, not the app session.
        //
        // This endpoint asks "are you in this meeting?", not "who are you?" —
        // a guest holds no app session at all, and a signed-in participant's
        // access token expires after 15 minutes, long before a meeting is over.
        // The room token answers the question actually being asked, and every
        // participant has one for as long as they are in the room.
        headers: { Authorization: `Bearer ${roomToken}` },
      },
    );
  },

  /**
   * End the meeting for everyone, on the record.
   *
   * The channel broadcast that disconnects the room is ephemeral — it tells the
   * people currently connected to leave and nothing else. Without this the
   * meeting row is never marked ended, so anybody can walk straight back into
   * the room the host has just closed.
   */
  endForAll(id: string) {
    return apiFetch<{ ok: boolean }>(`/api/v1/meetings/${id}/end`, { method: 'POST' });
  },

  /** `scope: 'all'` on a day of a series cancels every day that has not happened yet. */
  delete(id: string, opts: { scope?: 'one' | 'all' } = {}) {
    const query = opts.scope === 'all' ? '?scope=all' : '';
    return apiFetch<{ ok: boolean }>(`/api/v1/meetings/${id}${query}`, { method: 'DELETE' });
  },

  /**
   * A recurring meeting's calendar file (the one its emails carry) and its
   * repeat rule, for "Add to calendar". 404s for a one-off meeting.
   */
  calendar(id: string) {
    return apiFetch<{ ics: string; rrule: string }>(`/api/v1/meetings/${id}/calendar`);
  },

  generateInviteLink(id: string) {
    return apiFetch<{ token: string; inviteUrl: string }>(`/api/v1/meetings/${id}/invite-link`, {
      method: 'POST',
    });
  },
};

/** A vote the caller has already cast, returned alongside the polls. */
type OwnPollVote = { pollId: string; participantId: string; optionIds: string[] }

/**
 * Meeting polls. The server owns them: every call returns the poll with its
 * tallies already folded in, and the room channel pushes the same shape to
 * everyone else as `poll:created` / `poll:updated` / `poll:ended`.
 */
export const pollsApi = {
  /** Every poll for the meeting — how a late joiner or a reload catches up. */
  list(meetingId: string) {
    return apiFetch<{ polls: any[]; myVotes?: OwnPollVote[] }>(
      `/api/v1/meetings/${encodeURIComponent(meetingId)}/polls`,
    );
  },

  create(
    meetingId: string,
    body: {
      question: string
      type: 'single' | 'multiple' | 'rating'
      options: string[]
      anonymous: boolean
      maxSelections?: number
    },
  ) {
    return apiFetch<{ poll: any }>(
      `/api/v1/meetings/${encodeURIComponent(meetingId)}/polls`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /**
   * List and vote as a room participant, using the ROOM token.
   *
   * This is the path every participant uses, guest or signed in. The authed
   * equivalents below need an app session, which a guest joining by link does
   * not have — they were shown the poll, voted, and got a silent 401.
   *
   * Using one path for everyone also keeps one identity per participant. The
   * authed route records the app user id and this one records the room token's
   * `sub`; letting a client pick between them would let the same person vote
   * twice, since the unique index is per voter id.
   */
  listByRoom(roomId: string, roomToken: string) {
    return fetch(`${API}/api/v1/meeting-polls/${encodeURIComponent(roomId)}/polls`, {
      headers: { Authorization: `Bearer ${roomToken}` },
    }).then(async (res) => {
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message || 'Failed to load polls');
      return body as { polls: any[]; myVotes?: OwnPollVote[] };
    });
  },

  voteByRoom(roomId: string, roomToken: string, pollId: string, optionIds: string[]) {
    return fetch(
      `${API}/api/v1/meeting-polls/${encodeURIComponent(roomId)}/polls/${encodeURIComponent(pollId)}/vote`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${roomToken}` },
        body: JSON.stringify({ optionIds }),
      },
    ).then(async (res) => {
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message || 'Failed to record vote');
      return body as { poll: any };
    });
  },

  /**
   * Create and end with the ROOM token. The server still checks the caller's
   * live role (host or co-host); the token only proves they are in the room.
   * Needed because a co-host promoted from a guest has no app session, so the
   * authed `create`/`end` answered them with 401.
   */
  createByRoom(
    roomId: string,
    roomToken: string,
    body: {
      question: string
      type: 'single' | 'multiple' | 'rating'
      options: string[]
      anonymous: boolean
      maxSelections?: number
    },
  ) {
    return fetch(`${API}/api/v1/meeting-polls/${encodeURIComponent(roomId)}/polls`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${roomToken}` },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const resBody = await res.json();
      if (!res.ok) throw new Error(resBody?.error?.message || 'Failed to create poll');
      return resBody as { poll: any };
    });
  },

  endByRoom(roomId: string, roomToken: string, pollId: string) {
    return fetch(
      `${API}/api/v1/meeting-polls/${encodeURIComponent(roomId)}/polls/${encodeURIComponent(pollId)}/end`,
      { method: 'POST', headers: { Authorization: `Bearer ${roomToken}` } },
    ).then(async (res) => {
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message || 'Failed to end poll');
      return body as { poll: any };
    });
  },

  /** The voter is taken from the token server-side, never sent from here. */
  vote(meetingId: string, pollId: string, optionIds: string[]) {
    return apiFetch<{ poll: any }>(
      `/api/v1/meetings/${encodeURIComponent(meetingId)}/polls/${encodeURIComponent(pollId)}/vote`,
      { method: 'POST', body: JSON.stringify({ optionIds }) },
    );
  },

  end(meetingId: string, pollId: string) {
    return apiFetch<{ poll: any }>(
      `/api/v1/meetings/${encodeURIComponent(meetingId)}/polls/${encodeURIComponent(pollId)}/end`,
      { method: 'POST' },
    );
  },
};

export type GuestMeeting = { id: string; meetingId?: string | null; title: string; host_name: string; settings: any };

/**
 * Why a joiner is waiting. `'host'`: "join at any time" is off and no host has
 * arrived — they go in when one does. `'admission'`: a lock or waiting room —
 * the host has to let them in. Absent from an older server; treat as admission.
 */
export type WaitingFor = 'host' | 'admission';

/**
 * Signaling's lobby channel for a request that is waiting: open `signalingWsUrl`
 * with `token`, join `topic`, and the decision is pushed (`lobby-watch`).
 * Absent from a server that predates it — poll `lobbyStatus` instead.
 */
export type LobbyWait = { token: string; topic: string; signalingWsUrl: string };

export type GuestJoinResult =
  | { status: 'admitted'; meeting: GuestMeeting; guestId: string; roomCredentials: any }
  | { status: 'waiting'; requestId: string; waitingFor?: WaitingFor; meeting: GuestMeeting; lobby?: LobbyWait };

/** The meeting as a Screen Share join describes it: a summary, never the invitees. */
export type ShareJoinMeeting = { id: string; meetingId: string | null; title: string; hostName: string };

/**
 * `mode` is optional in the type because a server without share mode leaves it
 * out — and its answer must then not be used (see `joinShare`).
 */
export type ShareJoinResult =
  | { status: 'ok'; mode?: 'share'; meeting: ShareJoinMeeting | null }
  | {
      status: 'waiting'
      mode?: 'share'
      shareId: string
      requestId: string
      waitingFor?: WaitingFor
      meeting: ShareJoinMeeting
      lobby?: LobbyWait
    }
  | {
      status: 'admitted'
      mode?: 'share'
      shareId: string
      meeting: ShareJoinMeeting
      roomCredentials: RoomCredentials
    };

export type LobbyStatusResult =
  | { status: 'pending' }
  | { status: 'admitted'; roomCredentials: any }
  | { status: 'denied' }
  /** Admitted, but every seat was taken by then. */
  | { status: 'full' };

// Guest API — no auth token required
export const guestApi = {
  getInvitePreview(token: string) {
    return fetch(`${API}/api/v1/guest/invite/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) throw new Error(body?.error?.message || 'Invalid invite link');
        return body as { meeting: { id: string; meetingId?: string | null; title: string; host_name: string } };
      });
  },

  guestJoin(token: string, name: string) {
    return retryWhileSignalingBusy(() => fetch(`${API}/api/v1/guest/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, name }),
    }).then(async (res) => {
      const body = await res.json();
      // ApiError, like `guestJoinOpen`, so the caller can tell MEETING_FULL
      // from everything else.
      if (!res.ok) {
        throw new ApiError(
          body?.error?.message || 'Failed to join',
          body?.error?.code || 'API_ERROR',
          res.status,
        );
      }
      return body as { meeting: GuestMeeting; guestId: string; roomCredentials: any };
    }));
  },

  /**
   * Check a password without joining, so a guest can be asked for it before
   * the prejoin preview. `join-open` cannot serve this: it needs a name that
   * has not been collected yet, and it files a lobby request as a side effect.
   */
  verifyMeetingPassword(meetingId: string, password: string) {
    return fetch(`${API}/api/v1/guest/meeting/${encodeURIComponent(meetingId)}/verify-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    }).then(async (res) => {
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new ApiError(
          body?.error?.message || 'Incorrect meeting password',
          body?.error?.code || 'API_ERROR',
          res.status,
        );
      }
      return body as { ok: true };
    });
  },

  getMeetingPreview(meetingId: string) {
    return fetch(`${API}/api/v1/guest/meeting/${encodeURIComponent(meetingId)}`)
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) {
          throw new ApiError(
            body?.error?.message || 'Meeting not found',
            body?.error?.code || 'API_ERROR',
            res.status,
            body?.error && typeof body.error === 'object' ? body.error : {},
          );
        }
        // `passwordRequired` lets the caller ask for the password up front
        // instead of discovering it from a refused join.
        return body as {
          meeting: {
            id: string
            /** The 9-digit code, when the meeting has one. */
            meetingId?: string | null
            title: string
            host_name: string
            passwordRequired?: boolean
          }
        };
      });
  },

  /**
   * `password` is only needed for a protected meeting. Failures throw an
   * ApiError rather than a bare Error so the caller can tell INVALID_PASSWORD
   * — which means "ask for it" — from everything else, which means "give up".
   */
  guestJoinOpen(meetingId: string, name: string, password?: string): Promise<GuestJoinResult> {
    return retryWhileSignalingBusy(() => fetch(`${API}/api/v1/guest/join-open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ meetingId, name, ...(password ? { password } : {}) }),
    }).then(async (res) => {
      const body = await res.json();
      if (!res.ok) {
        throw new ApiError(
          body?.error?.message || 'Failed to join',
          body?.error?.code || 'API_ERROR',
          res.status,
          body?.error && typeof body.error === 'object' ? body.error : {},
        );
      }
      return body as GuestJoinResult;
    }));
  },

  /**
   * Poll a waiting guest's request.
   *
   * Throws an ApiError carrying the HTTP status, for the same reason
   * `guestJoinOpen` does: the caller has to tell a request that is GONE from a
   * network blip. A bare Error made those identical, and the poll treats
   * anything it cannot identify as a blip and keeps going — so a guest whose
   * request had been decided and dropped sat in the waiting room for ever,
   * watching a spinner that would never resolve.
   */
  lobbyStatus(requestId: string): Promise<LobbyStatusResult> {
    return fetch(`${API}/api/v1/guest/lobby-status/${encodeURIComponent(requestId)}`)
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          throw new ApiError(
            body?.error?.message || 'Request not found',
            body?.error?.code || 'API_ERROR',
            res.status,
          );
        }
        return body as LobbyStatusResult;
      });
  },
};

/** A person waiting for host admission. */
export type LobbyRequest = {
  id: string
  name: string
  guestId?: string
  /** `"share"`: a Screen Share page waiting to come in as a screen only (contract §5). */
  mode?: string | null
  /** The account email a share page knocked with; null for anyone else. */
  email?: string | null
}

// Hosts read and decide the waiting room on the room channel (useLobby):
// `lobby:updated` / `room:lock_changed` are pushed, `lobby:admit`, `lobby:deny`
// and `lobby:lock` are sent there. The backend's lobby routes remain only for
// clients that predate that.

